import { useState, useEffect, useRef, useContext } from "react";
import {
  useAuthContext,
  AuthContext,
  YearContext,
} from "../../context/context";
import { addGoal, fetchCategories } from "../../api";
import BtnPrimaryL from "../Buttons/BtnPrimaryL";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import InputText from "../Inputs/InputText";
import InputCheckbox from "../Inputs/InputCheckbox";
import InputDate from "../Inputs/InputDate";
import InputFile from "../Inputs/InputFile";
import InputSelect from "../Inputs/InputSelect";
import ToggleDropdown from "../Elements/ToggleDropdown";
import IconCircle from "../Elements/IconCircle";
import { HiOutlineChevronDoubleRight } from "react-icons/hi";
import { IoCloseOutline } from "react-icons/io5";
import Subheadline from "../Elements/Subheadline";
import DropdownInput from "../Inputs/DropdownInput";
import { useLanguage } from "../../context/LanguageContext";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "../../context/ToastContext";
import { FiPlus } from "react-icons/fi";
import PropTypes from "prop-types";

const formatDate = (date) => {
  if (!date) return "";
  if (typeof date === "string") {
    let parsedDate;
    // Check for dd/mm/yyyy format
    if (date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = date.split("/").map(Number);
      parsedDate = new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      // Assume YYYY-MM-DD or other parsable string
      parsedDate = new Date(date);
    }

    if (!isNaN(parsedDate)) {
      date = parsedDate;
    } else {
      return ""; // Invalid string date
    }
  }

  if (date instanceof Date) {
    // Return YYYY-MM-DD for input value using local time
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  return "";
};

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

// Extracted form component to eliminate duplication
function GoalForm({
  register,
  handleSubmit,
  setValue,
  watch,
  errors,
  onSubmit,
  onError,
  milestones,
  handleMilestoneChange,
  addMilestone,
  removeMilestone,
  categoryDropdownRef,
  categoryDropdown,
  setCategoryDropdown,
  filteredYears,
  filteredCategories,
  capitalizeCategory,
  formatDate,
  copy,
  isMobile,
  handleCategoryInputChange,
}) {
  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex flex-col gap-8"
    >
      <div className="flex flex-col gap-4 w-full">
        <InputText
          label={copy.addgoal.form.label_title}
          {...register("goal_title")}
          error={errors.goal_title?.message}
          details={copy.addgoal.form.details_goal_title.replace(
            "{count}",
            watch("goal_title")?.length || 0
          )}
        />
        <InputFile
          label={copy.addgoal.form.label_img}
          {...register("goal_img")}
          onChange={(e) => {
            setValue("goal_img", e.target.files);
          }}
          error={errors.goal_img?.message}
        />

        <InputSelect
          label={copy.addgoal.form.label_deadline}
          id="year"
          value={watch("goal_year") || ""}
          onChange={(e) => setValue("goal_year", Number(e.target.value))}
          error={errors.goal_year?.message}
          options={filteredYears
            .sort((a, b) => a - b)
            .map(year => ({
              label: year.toString(),
              value: year.toString()
            }))}
        />

        <div className="relative" ref={categoryDropdownRef}>
          <InputText
            label={copy.addgoal.form.label_category}
            id="category"
            value={watch("goal_category")}
            onChange={handleCategoryInputChange}
            onFocus={() => setCategoryDropdown(true)}
            error={errors.goal_category?.message}
            details={copy.addgoal.form.details_goal_category.replace(
              "{count}",
              watch("goal_category")?.length || 0
            )}
          />
          {categoryDropdown && filteredCategories.length > 0 && (
            <ul className="absolute bg-white card-base-border rounded-md shadow-lg z-10 mt-1 w-full">
              {filteredCategories.map((category, idx) => (
                <DropdownInput
                  key={idx}
                  value={capitalizeCategory(category)}
                  onSelect={(val) => {
                    setValue("goal_category", capitalizeCategory(val));
                    setCategoryDropdown(false);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <Subheadline
            subheadline={copy.addgoal.milestone_title}
            description={copy.addgoal.milesone_description}
          />
        </div>

        {milestones.map((milestone, index) => (
          <div
            key={index}
            className="bg-white border card-base-border rounded-md p-4 relative"
          >
            <div className="max-w-[90%] flex flex-col gap-3">
              <InputText
                label={copy.addgoal.form.label_milestone_title}
                value={milestone.title}
                onChange={(e) =>
                  handleMilestoneChange(index, "title", e.target.value)
                }
                error={errors.milestones?.[index]?.title?.message}
                details={copy.addgoal.form.details_milestone_title.replace(
                  "{count}",
                  milestone.title?.length || 0
                )}
              />
              <InputDate
                label={copy.addgoal.form.label_milestone_deadline}
                value={formatDate(milestone.deadline)}
                onChange={(e) =>
                  handleMilestoneChange(index, "deadline", e.target.value)
                }
                error={errors.milestones?.[index]?.deadline?.message}
                details={copy.addgoal.form.details_milestone_deadline.replace(
                  "{count}",
                  formatDate(milestone.deadline).length
                )}
              />
              <div
                className={`${isMobile ? "flex flex-col" : "flex flex-row"} gap-2`}
              >
                <div className="flex gap-2 items-center">
                  <InputCheckbox
                    label={copy.addgoal.form.label_milestone_repeat}
                    checked={milestone.repeat}
                    onChange={(e) =>
                      handleMilestoneChange(index, "repeat", e.target.checked)
                    }
                  />
                  {milestone.repeat && (
                    <InputSelect
                      value={milestone.repeatInterval}
                      onChange={(e) =>
                        handleMilestoneChange(
                          index,
                          "repeatInterval",
                          e.target.value
                        )
                      }
                      options={[
                        {
                          label: copy.addgoal.form.label_milestone_repeat_1,
                          value: "days",
                        },
                        {
                          label: copy.addgoal.form.label_milestone_repeat_2,
                          value: "weeks",
                        },
                        {
                          label: copy.addgoal.form.label_milestone_repeat_3,
                          value: "months",
                        },
                        {
                          label: copy.addgoal.form.label_milestone_repeat_4,
                          value: "quarters",
                        },
                      ]}
                      error={
                        errors.milestones?.[index]?.repeatInterval?.message
                      }
                    />
                  )}
                </div>
                <div
                  className={`flex gap-2 items-center ${isMobile ? "!ml-8" : ""}`}
                >
                  {milestone.repeat && (
                    <>
                      <p>{copy.addgoal.form.label_milestone_repeat_until}</p>
                      <InputDate
                        value={formatDate(milestone.repeatUntil)}
                        onChange={(e) =>
                          handleMilestoneChange(
                            index,
                            "repeatUntil",
                            e.target.value
                          )
                        }
                        error={errors.milestones?.[index]?.repeatUntil?.message}
                        details={copy.addgoal.form.details_milestone_repeat_until.replace(
                          "{count}",
                          milestone.repeatUntil
                            ? formatDate(milestone.repeatUntil).length
                            : 0
                        )}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="absolute top-2 right-2">
              <IconCircle
                icon={IoCloseOutline}
                onClick={() => removeMilestone(index)}
                disabled={milestones.length === 1}
              />
            </div>
          </div>
        ))}
        <div className="flex justify-end">
          <BtnSecondaryM
            buttonCTA={
              isMobile ? <FiPlus size={20} /> : copy.addgoal.button_milestone
            }
            type="button"
            onClickLink={addMilestone}
            className={
              isMobile
                ? "w-10 h-10 rounded-full flex items-center justify-center p-0"
                : ""
            }
          />
        </div>
       
      </div>

      <div className="flex self-center mt-10">
        <BtnPrimaryL
          buttonCTA={copy.addgoal.button_add_goal}
          type="submit"
          className="w-full"
        />
      </div>
    </form>
  );
}

GoalForm.propTypes = {
  register: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  milestones: PropTypes.array.isRequired,
  handleMilestoneChange: PropTypes.func.isRequired,
  addMilestone: PropTypes.func.isRequired,
  removeMilestone: PropTypes.func.isRequired,
  categoryDropdownRef: PropTypes.object.isRequired,
  categoryDropdown: PropTypes.bool.isRequired,
  setCategoryDropdown: PropTypes.func.isRequired,
  filteredYears: PropTypes.array.isRequired,
  filteredCategories: PropTypes.array.isRequired,
  capitalizeCategory: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  copy: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  handleCategoryInputChange: PropTypes.func.isRequired,
};

export default function NavSidebar({ setSidebarToggled }) {
  const { userData } = useAuthContext();
  const { setUserData } = useContext(AuthContext);
  const { years: contextYears } = useContext(YearContext);
  const { copy } = useLanguage();
  const { showToast } = useToast();

  const [categories, setCategories] = useState([]);
  const [filteredYears, setFilteredYears] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  const milestoneSchema = z
    .object({
      title: z.string().min(1, copy.addgoal.errors.milestone_title_required),
      deadline: z
        .string()
        .min(1, copy.addgoal.errors.milestone_deadline_required),
      repeat: z.boolean(),
      repeatInterval: z.string().optional(),
      repeatUntil: z.string().optional(),
    })
    .refine(
      (data) => {
        if (data.repeat) {
          return !!data.repeatInterval;
        }
        return true;
      },
      {
        path: ["repeatInterval"],
        message: copy.addgoal.errors.milestone_repeat_interval_required,
      }
    )
    .refine(
      (data) => {
        if (data.repeat && data.repeatInterval) {
          return !!data.repeatUntil;
        }
        return true;
      },
      {
        path: ["repeatUntil"],
        message: copy.addgoal.errors.milestone_repeat_until_required,
      }
    );

  const goalFormSchema = z.object({
    goal_title: z.string().min(1, copy.addgoal.errors.goal_title_required),
    goal_img: z
      .any()
      .refine((files) => {
        return files?.length === 1;
      }, copy.addgoal.errors.goal_img_required)
      .refine(
        (files) => files?.[0]?.size < 5 * 1024 * 1024,
        copy.addgoal.errors.goal_img_too_large
      ),
    goal_year: z
      .number({ invalid_type_error: copy.addgoal.errors.goal_year_invalid })
      .int(copy.addgoal.errors.goal_year_integer)
      .gte(1000, copy.addgoal.errors.goal_year_4_digits)
      .lte(9999, copy.addgoal.errors.goal_year_4_digits),
    goal_category: z
      .string()
      .min(1, copy.addgoal.errors.goal_category_required),
    milestones: z
      .array(milestoneSchema)
      .min(1, copy.addgoal.errors.milestones_required),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(goalFormSchema),
    defaultValues: {
      goal_title: "",
      goal_img: undefined,
      goal_year: undefined,
      goal_category: "",
      milestones: [
        {
          title: "",
          deadline: "",
          repeat: false,
          repeatInterval: "",
          repeatUntil: formatDate(new Date(new Date().getFullYear(), 11, 31)),
        },
      ],
    },
  });

  const milestones = watch("milestones");

  const isMobile = useIsMobile();

  useEffect(() => {
    const getYearsAndCategories = async () => {
      try {
        // Use years from context instead of fetching, ensuring 2025 and 2026 are always available
        const currentYear = new Date().getFullYear();
        const fallbackYears = [currentYear, currentYear + 1, currentYear + 2];
        const allYears = [...new Set([...contextYears, ...fallbackYears])];
        setFilteredYears(allYears);

        let categoriesData = await fetchCategories();
        // Deduplicate categories (case-insensitive)
        const seen = new Set();
        categoriesData = categoriesData.filter((cat) => {
          const lower = cat.toLowerCase();
          if (seen.has(lower)) return false;
          seen.add(lower);
          return true;
        });
        setCategories(categoriesData);
        setFilteredCategories(categoriesData);
      } catch (err) {
        console.error("Error fetching dropdowns:", err);
      }
    };
    getYearsAndCategories();
  }, [contextYears]); // Add contextYears as dependency



  const handleCategoryInputChange = (e) => {
    let inputValue = e.target.value;
    // Capitalize first letter
    if (inputValue.length > 0) {
      inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
    }
    setValue("goal_category", inputValue);
    const filtered = categories.filter((cat) =>
      cat.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredCategories(filtered);
    setCategoryDropdown(filtered.length > 0);
  };

  const capitalizeCategory = (category) => {
    return category
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleMilestoneChange = (index, field, value) => {
    const updated = [...milestones];
    updated[index][field] = value;
    // If enabling repeat and repeatUntil is empty, set to last day of year
    if (field === "repeat" && value === true) {
      const year = watch("goal_year") || new Date().getFullYear();
      if (!updated[index].repeatUntil) {
        updated[index].repeatUntil = formatDate(new Date(year, 11, 31));
      }
    }
    setValue("milestones", updated);
  };

  const addMilestone = () => {
    const currentGoalYear = watch("goal_year");
    const defaultRepeatUntil = currentGoalYear
      ? formatDate(new Date(currentGoalYear, 11, 31))
      : formatDate(new Date(new Date().getFullYear(), 11, 31)); // Default to end of current year if goal_year is not set
    setValue("milestones", [
      ...milestones,
      {
        title: "",
        deadline: "",
        repeat: false,
        repeatInterval: "",
        repeatUntil: defaultRepeatUntil,
      },
    ]);
  };

  const removeMilestone = (index) => {
    if (milestones.length > 1) {
      const updated = milestones.filter((_, i) => i !== index);
      setValue("milestones", updated);
    }
  };

  const onSubmit = async (formData) => {
    // console.log("🎯 Starting goal submission...");
    const expandedMilestones = [];
    const MAX_REPEATS = 366;

    formData.milestones.forEach((milestone) => {
      if (milestone.repeat && milestone.repeatInterval) {
        let startDate = new Date(milestone.deadline);
        const originalDay = startDate.getDate();
        const repeatUntilDate = milestone.repeatUntil
          ? new Date(milestone.repeatUntil + "T23:59:59")
          : new Date(`${formData.goal_year}-12-31T23:59:59`); // Use repeatUntil or end of year
        let repeatCount = 0;
        let lastAddedDate = null;

        while (startDate <= repeatUntilDate && repeatCount < MAX_REPEATS) {
          expandedMilestones.push({
            ...milestone,
            deadline: formatDate(new Date(startDate)),
            repeat: false,
            repeatInterval: undefined,
            repeatUntil: undefined, // Clear repeatUntil for expanded milestones
            status: milestone.status || false,
          });

          lastAddedDate = formatDate(new Date(startDate));
          repeatCount++;
          const nextDate = new Date(startDate);

          switch (milestone.repeatInterval) {
            case "days":
              nextDate.setDate(nextDate.getDate() + 1);
              break;
            case "weeks":
              nextDate.setDate(nextDate.getDate() + 7);
              break;
            case "months":
              nextDate.setMonth(nextDate.getMonth() + 1);
              // Handle month transitions properly
              if (nextDate.getDate() !== originalDay) {
                nextDate.setDate(
                  Math.min(
                    originalDay,
                    new Date(
                      nextDate.getFullYear(),
                      nextDate.getMonth() + 1,
                      0
                    ).getDate()
                  )
                );
              }
              break;
            case "quarters":
              nextDate.setMonth(nextDate.getMonth() + 3);
              // Handle month transitions properly for quarterly intervals
              if (nextDate.getDate() !== originalDay) {
                nextDate.setDate(
                  Math.min(
                    originalDay,
                    new Date(
                      nextDate.getFullYear(),
                      nextDate.getMonth() + 1,
                      0
                    ).getDate()
                  )
                );
              }
              break;
            default:
              break;
          }

          // Update startDate for next iteration
          startDate = new Date(nextDate);
        }
        // Always include the repeatUntil date if not already present
        const formattedRepeatUntil = formatDate(repeatUntilDate);
        if (
          expandedMilestones.length > 0 &&
          expandedMilestones[expandedMilestones.length - 1].deadline !== formattedRepeatUntil
        ) {
          expandedMilestones.push({
            ...milestone,
            deadline: formattedRepeatUntil,
            repeat: false,
            repeatInterval: undefined,
            repeatUntil: undefined,
            status: milestone.status || false,
          });
        }
      } else {
        expandedMilestones.push({
          ...milestone,
          deadline: formatDate(milestone.deadline),
          status: milestone.status || false,
        });
      }
    });

    // --- Optimistic UI update ---
    // Generate a temporary ID for the optimistic goal
    const tempGoalId = `temp-${Date.now()}`;
    // Prepare optimistic goal object (structure based on usage in app)
    const optimisticGoal = {
      id: tempGoalId,
      title: formData.goal_title,
      img: formData.goal_img?.[0] || "",
      year: formData.goal_year,
      category: formData.goal_category,
      // Add any other fields your UI expects
    };
    // Prepare optimistic milestones (add temp goal_id and temp ids)
    const optimisticMilestones = expandedMilestones.map((m, idx) => ({
      ...m,
      id: `${tempGoalId}-m${idx}`,
      goal_id: tempGoalId,
    }));

    // Optimistically update UI
    setUserData((prevData) => ({
      ...prevData,
      goals: [...(prevData.goals || []), optimisticGoal],
      milestones: [...(prevData.milestones || []), ...optimisticMilestones],
    }));
    setSidebarToggled(false);
    // Show toast immediately (optimistic)
    showToast("Goal was successfully added.", "success");

    try {
      // Prepare FormData for API
      const goalData = new FormData();
      goalData.append("user_id", userData.id);
      goalData.append("goal_title", formData.goal_title);
      goalData.append("goal_img", formData.goal_img?.[0] || "");
      goalData.append("goal_year", formData.goal_year);
      goalData.append("goal_category", formData.goal_category);
      goalData.append("milestones", JSON.stringify(expandedMilestones));

      const data = await addGoal(goalData);

      if (data && data.goal) {
        // Replace the optimistic goal and milestones with real ones from the server
        setUserData((prevData) => ({
          ...prevData,
          goals: [
            ...(prevData.goals || []).filter((g) => g.id !== tempGoalId),
            data.goal,
          ],
          milestones: [
            ...(prevData.milestones || []).filter(
              (m) => m.goal_id !== tempGoalId
            ),
            ...data.milestones,
          ],
        }));
        // No need to show success toast again here
      } else {
        throw new Error("Invalid data from server");
      }
    } catch (err) {
      // Revert optimistic update
      setUserData((prevData) => ({
        ...prevData,
        goals: (prevData.goals || []).filter((g) => g.id !== tempGoalId),
        milestones: (prevData.milestones || []).filter(
          (m) => m.goal_id !== tempGoalId
        ),
      }));
      showToast("Failed to add goal. Please try again.", "error");
    }
  };

  const onError = (err) => {
    console.error("Validation errors:", err);
  };

  useEffect(() => {
    const currentGoalYear = watch("goal_year");
    if (currentGoalYear) {
      // Only update milestones' repeatUntil if it matches the previous year's end date
      // This means it's still the default suggestion and should be updated
      const updatedMilestones = milestones.map((milestone) => {
        const previousYear = currentGoalYear - 1;
        const previousYearEnd = formatDate(new Date(previousYear, 11, 31));
        
        // Only update if the current repeatUntil is the end of the previous year
        // This means it's still the default suggestion
        if (milestone.repeatUntil === previousYearEnd) {
          const newRepeatUntil = formatDate(new Date(currentGoalYear, 11, 31));
          return {
            ...milestone,
            repeatUntil: newRepeatUntil,
          };
        }
        return milestone;
      });

      // Only call setValue if there were actual changes
      if (JSON.stringify(updatedMilestones) !== JSON.stringify(milestones)) {
        setValue("milestones", updatedMilestones);
      }
    }
  }, [watch("goal_year"), setValue, milestones]);

  // useEffect(() => {
  //   const currentGoalYear = watch("goal_year");
  //   if (currentGoalYear && milestones.length > 0) {
  //     const updatedMilestones = milestones.map((milestone) => {
  //       // Only update milestones that have repeat enabled
  //       if (milestone.repeat) {
  //         return {
  //           ...milestone,
  //           repeatUntil: formatDate(new Date(currentGoalYear, 11, 31))
  //         };
  //       }
  //       return milestone;
  //     });
      
  //     setValue("milestones", updatedMilestones);
  //   }
  // }, [watch("goal_year"), milestones, setValue]);

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (
  //       yearDropdownRef.current &&
  //       !yearDropdownRef.current.contains(event.target)
  //     ) {
  //       setYearDropdown(false);
  //     }
  //     if (
  //       categoryDropdownRef.current &&
  //       !categoryDropdownRef.current.contains(event.target)
  //     ) {
  //       setCategoryDropdown(false);
  //     }
  //   };
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, []);

  // Common content for both mobile and desktop
  const commonContent = (
    <>
      <ToggleDropdown
        title={copy.addgoal.toggle_title}
        content={[
          <p key="s"><strong>S:</strong> {copy.addgoal.smart.s}</p>,
          <p key="m"><strong>M:</strong> {copy.addgoal.smart.m}</p>,
          <p key="a"><strong>A:</strong> {copy.addgoal.smart.a}</p>,
          <p key="r"><strong>R:</strong> {copy.addgoal.smart.r}</p>,
          <p key="t"><strong>T:</strong> {copy.addgoal.smart.t}</p>,
          <p
            key="help"
            className="text-sm !mt-2"
            dangerouslySetInnerHTML={{ __html: copy.addgoal.smart.help }}
          />,
        ]}
      />
      <GoalForm
        register={register}
        handleSubmit={handleSubmit}
        setValue={setValue}
        watch={watch}
        errors={errors}
        onSubmit={onSubmit}
        onError={onError}
        milestones={milestones}
        handleMilestoneChange={handleMilestoneChange}
        addMilestone={addMilestone}
        removeMilestone={removeMilestone}
        categoryDropdownRef={categoryDropdownRef}
        categoryDropdown={categoryDropdown}
        setCategoryDropdown={setCategoryDropdown}
        filteredYears={filteredYears}
        filteredCategories={filteredCategories}
        capitalizeCategory={capitalizeCategory}
        formatDate={formatDate}
        copy={copy}
        isMobile={isMobile}
        handleCategoryInputChange={handleCategoryInputChange}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end md:hidden bg-black/30">
        <div className="relative w-full bg-white rounded-t-xl shadow-2xl p-4 pb-8 card-base-border animate-slide-up max-h-[90vh] overflow-y-auto">
          {/* X button */}
          <div className="flex justify-end z-20 !mb-2">
            <IconCircle
              icon={IoCloseOutline}
              onClick={() => setSidebarToggled(false)}
              ariaLabel="Close"
            />
          </div>
          <div className="flex flex-col gap-8 mt-4 !mb-15">{commonContent}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end bg-black/30">
      <div className="relative h-screen w-1/2 overflow-y-scroll z-50 px-6 sm:px-12 md:px-16 lg:px-24 xl:px-30 py-6 sm:py-8 md:py-10 lg:py-12 bg-white shadow-lg card-base-border-left">
        <div className="gap-2 absolute top-2 left-3 sm:left-6 md:left-8 lg:left-12">
          <IconCircle
            icon={HiOutlineChevronDoubleRight}
            onClick={() => setSidebarToggled(false)}
          />
        </div>
        <div className="flex flex-col gap-8 mt-8 !mb-10">{commonContent}</div>
      </div>
    </div>
  );
}

NavSidebar.propTypes = {
  setSidebarToggled: PropTypes.func.isRequired,
};
